import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import Cta from '@/components/cta';
import { Partners } from '@/components/partners';

export default function PrivacyPolicy() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(0);

  // Section definitions (title and content as render functions)
  type Section = {
    key: string;
    title: string;
    content: JSX.Element;
    subtitles?: string[];
  };

  const sections: Section[] = [
    {
      key: 'dataCollected',
      title: t('privacy.dataCollected.title'),
      subtitles: [
        t('privacy.dataCollected.whenController.title'),
        t('privacy.dataCollected.whenController.customers.title'),
        t('privacy.dataCollected.whenController.suppliers.title'),
        t('privacy.dataCollected.whenProcessor.title'),
      ],
      content: (
        <>
          <h2 id="section-dataCollected" className="text-2xl font-semibold mb-4">{t('privacy.dataCollected.title')}</h2>
          <h3 id="section-dataCollected-sub-0" className="text-xl font-medium mt-6 mb-3">{t('privacy.dataCollected.whenController.title')}</h3>
          <h4 className="font-semibold mt-4 mb-2">{t('privacy.dataCollected.whenController.interactions.title')}</h4>
          <p className="mb-2"><strong>{t('privacy.dataCollected.source')}:</strong></p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>{t('privacy.dataCollected.whenController.interactions.sources.contactForm')}</li>
            <li>{t('privacy.dataCollected.whenController.interactions.sources.requests')}</li>
            <li>{t('privacy.dataCollected.whenController.interactions.sources.applications')}</li>
          </ul>
          <p className="mb-2"><strong>{t('privacy.dataCollected.categories')}:</strong></p>
          <p className="mb-4">{t('privacy.dataCollected.whenController.interactions.categories')}</p>
          <p className="mb-2"><strong>{t('privacy.dataCollected.purposes')}:</strong></p>
          <p className="mb-4">{t('privacy.dataCollected.whenController.interactions.purposes')}</p>
          <p className="mb-2"><strong>{t('privacy.dataCollected.legalBasis')}:</strong></p>
          <p className="mb-6">{t('privacy.dataCollected.whenController.interactions.legalBasis')}</p>
          <h4 id="section-dataCollected-sub-1" className="font-semibold mt-6 mb-2">{t('privacy.dataCollected.whenController.customers.title')}</h4>
          <p className="mb-2"><strong>{t('privacy.dataCollected.source')}:</strong></p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>{t('privacy.dataCollected.whenController.customers.sources.contract')}</li>
            <li>{t('privacy.dataCollected.whenController.customers.sources.exchanges')}</li>
          </ul>
          <p className="mb-2"><strong>{t('privacy.dataCollected.categories')}:</strong></p>
          <p className="mb-4">{t('privacy.dataCollected.whenController.customers.categories')}</p>
          <p className="mb-2"><strong>{t('privacy.dataCollected.purposes')}:</strong></p>
          <p className="mb-4">{t('privacy.dataCollected.whenController.customers.purposes')}</p>
          <p className="mb-2"><strong>{t('privacy.dataCollected.legalBasis')}:</strong></p>
          <p className="mb-6">{t('privacy.dataCollected.whenController.customers.legalBasis')}</p>
          <h4 id="section-dataCollected-sub-2" className="font-semibold mt-6 mb-2">{t('privacy.dataCollected.whenController.suppliers.title')}</h4>
          <p className="mb-2"><strong>{t('privacy.dataCollected.source')}:</strong></p>
          <p className="mb-4">{t('privacy.dataCollected.whenController.suppliers.source')}</p>
          <p className="mb-2"><strong>{t('privacy.dataCollected.categories')}:</strong></p>
          <p className="mb-4">{t('privacy.dataCollected.whenController.suppliers.categories')}</p>
          <p className="mb-2"><strong>{t('privacy.dataCollected.purposes')}:</strong></p>
          <p className="mb-4">{t('privacy.dataCollected.whenController.suppliers.purposes')}</p>
          <p className="mb-2"><strong>{t('privacy.dataCollected.legalBasis')}:</strong></p>
          <p className="mb-6">{t('privacy.dataCollected.whenController.suppliers.legalBasis')}</p>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 mb-6">
            <p className="text-yellow-700 dark:text-yellow-300">
              <strong>🟡 {t('privacy.dataCollected.specificity.title')}:</strong> {t('privacy.dataCollected.specificity.description')}
            </p>
          </div>
          <h3 id="section-dataCollected-sub-3" className="text-xl font-medium mt-8 mb-4">{t('privacy.dataCollected.whenProcessor.title')}</h3>
          <p className="mb-6">{t('privacy.dataCollected.whenProcessor.description')}</p>
        </>
      )
    },
    {
      key: 'cookies',
      title: t('privacy.cookies.title'),
      content: (
        <>
          <h2 className="text-2xl font-semibold mb-4">{t('privacy.cookies.title')}</h2>
          <p className="mb-6">{t('privacy.cookies.intro')}</p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('privacy.cookies.table.cookieName')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('privacy.cookies.table.type')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('privacy.cookies.table.function')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('privacy.cookies.table.duration')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-gray-700">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">_ga _gat _gid _gat_gtag_UA _196676977_1</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{t('privacy.cookies.table.types.analytical')}</td>
                  <td className="px-6 py-4 text-sm">{t('privacy.cookies.table.functions.analytical')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{t('privacy.cookies.durations.session')}</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">_gcl_au</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{t('privacy.cookies.table.types.advertising')}</td>
                  <td className="px-6 py-4 text-sm">{t('privacy.cookies.table.functions.advertising')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">90 {t('privacy.cookies.durations.days')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )
    },
    {
      key: 'recipients',
      title: t('privacy.recipients.title'),
      content: (
        <>
          <h2 className="text-2xl font-semibold mb-4">{t('privacy.recipients.title')}</h2>
          <p className="mb-4">{t('privacy.recipients.intro')}</p>
          <ul className="list-disc pl-6 mb-6 space-y-1">
            <li>{t('privacy.recipients.purposes.hosting')}</li>
            <li>{t('privacy.recipients.purposes.emails')}</li>
            <li>{t('privacy.recipients.purposes.crm')}</li>
          </ul>
          <div className="overflow-x-auto mb-6">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('privacy.recipients.table.provider')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('privacy.recipients.table.purpose')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-gray-700">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">Google Workspace</td>
                  <td className="px-6 py-4 text-sm">{t('privacy.recipients.table.purposes.googleWorkspace')}</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">RunPod</td>
                  <td className="px-6 py-4 text-sm">{t('privacy.recipients.table.purposes.runpod')}</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">Twilio</td>
                  <td className="px-6 py-4 text-sm">{t('privacy.recipients.table.purposes.twilio')}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mb-6">{t('privacy.recipients.authorities')}</p>
        </>
      )
    },
    {
      key: 'transfers',
      title: t('privacy.transfers.title'),
      content: (
        <>
          <h2 className="text-2xl font-semibold mb-4">{t('privacy.transfers.title')}</h2>
          <p className="mb-6">{t('privacy.transfers.description')}</p>
        </>
      )
    },
    {
      key: 'security',
      title: t('privacy.security.title'),
      content: (
        <>
          <h2 className="text-2xl font-semibold mb-4">{t('privacy.security.title')}</h2>
          <p className="mb-4">{t('privacy.security.intro')}</p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>{t('privacy.security.measures.accessControl')}</li>
            <li>{t('privacy.security.measures.incidentLogging')}</li>
            <li>{t('privacy.security.measures.backup')}</li>
            <li>{t('privacy.security.measures.audit')}</li>
            <li>{t('privacy.security.measures.testing')}</li>
          </ul>
        </>
      )
    },
    {
      key: 'yourRights',
      title: t('privacy.yourRights.title'),
      content: (
        <>
          <h2 className="text-2xl font-semibold mb-4">{t('privacy.yourRights.title')}</h2>
          <p className="mb-4">{t('privacy.yourRights.intro')}</p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>{t('privacy.yourRights.rights.access')}</li>
            <li>{t('privacy.yourRights.rights.rectification')}</li>
            <li>{t('privacy.yourRights.rights.erasure')}</li>
            <li>{t('privacy.yourRights.rights.objection')}</li>
            <li>{t('privacy.yourRights.rights.portability')}</li>
            <li>{t('privacy.yourRights.rights.withdraw')}</li>
            <li>{t('privacy.yourRights.rights.postMortem')}</li>
          </ul>
          <p className="mb-6">
            {t('privacy.yourRights.contact')}:
            <br />
            <a href="mailto:contact@security.olesolution.net" className="text-primary hover:underline">
              📧 contact@security.olesolution.net
            </a>
            <br />
            {t('privacy.yourRights.identityProof')}
          </p>
        </>
      )
    },
    {
      key: 'effectiveDate',
      title: t('privacy.effectiveDate.title'),
      content: (
        <>
          <h2 className="text-2xl font-semibold mb-4">{t('privacy.effectiveDate.title')}</h2>
          <p className="mb-6">
            {t('privacy.effectiveDate.text', { date: '22 mai 2025' })}
            <br />
            {t('privacy.effectiveDate.changes')}
          </p>
        </>
      )
    }
  ];

  // Jump to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <div className="w-full z-50 px-4 py-20 flex flex-col items-center">
        <h1 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-2">
          {t('privacy.title')}
        </h1>
        <p className="text-center text-base text-slate-600 dark:text-slate-300 max-w-7xl">
          {t('privacy.intro')}
        </p>
      </div>
      <div className="min-h-screen pt-10 px-2 sm:px-4 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6 relative">
          {/* Sidebar Navigation */}
          <nav
            className="md:w-64 w-full md:sticky md:top-24 flex-shrink-0 z-10 md:h-[calc(100vh-6rem)] md:overflow-y-auto md:bg-transparent md:p-0"
            style={{ scrollbarWidth: 'thin' }}
          >
            <ul className="flex md:flex-col flex-row overflow-x-auto gap-2 md:gap-3 md:overflow-y-auto md:space-y-1 md:bg-transparent p-2 md:p-0">
              {sections.map((section, idx) => (
                <li key={section.key} className="md:w-full">
                  <button
                    className={`w-full px-4 py-2 text-left transition focus:outline-none focus:ring-2 focus:ring-primary/50
            ${selected === idx ? 'font-bold text-primary underline' : 'font-medium text-slate-800 dark:text-slate-200 hover:text-primary/80 hover:underline'}`}
                    onClick={() => {
                      setSelected(idx);
                      const sectionEl = document.getElementById(`section-${section.key}`);
                      if (sectionEl) {
                        sectionEl.scrollIntoView({ behavior: 'auto', block: 'start' });
                      }
                    }}
                  >
                    {section.title}
                  </button>
                  {/* Render subtitles if present */}
                  {section.subtitles && Array.isArray(section.subtitles) && (
                    <ul className="ml-4 mt-1 space-y-1">
                      {section.subtitles.map((subtitle: string, subIdx: number) => (
                        <li key={subIdx}>
                          <button
                            className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-primary/80 hover:underline transition"
                            onClick={() => {
                              setSelected(idx);
                              const subSectionEl = document.getElementById(`section-${section.key}-sub-${subIdx}`);
                              if (subSectionEl) {
                                subSectionEl.scrollIntoView({ behavior: 'auto', block: 'start' });
                              }
                            }}
                          >
                            {subtitle}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Content Section */}
          <main className="flex-1 max-w-3xl mx-auto p-0 sm:p-0 min-h-[60vh] max-h-[80vh] overflow-y-auto">
            <div className="p-4 sm:p-8">
              {sections[selected].content}
            </div>
          </main>
        </div>
      </div>
      <Partners />
      <Cta />
      <Footer />
    
    </>
  );
};

